import type { SelectedOptions, SelectedOptionExpanded } from '../types';
import { Trash, CopySimple, Eye, Archive, ArrowArcLeft, PencilSimple } from '@phosphor-icons/react';
import { Trans } from '@lingui/macro';
import { Button, Tooltip } from 'components';
import { useTableContext } from 'features/misc/table/providers/TableProvider';

type SelectedOptionsProps = {
  options: SelectedOptionExpanded[];
  onClick: (selectedOption: SelectedOptions) => void;
};

type ButtonProps = {
  type: SelectedOptions;
  tooltip: string;
  disabled: boolean;
  onClick: (selectedOption: SelectedOptions) => void;
  color?: string;
};

const btnText = {
  delete: <Trans>Delete</Trans>,
  copy: <Trans>Copy</Trans>,
  read: <Trans>Mark as Read</Trans>,
  unread: <Trans>Mark as Unread</Trans>,
  archive: <Trans>Archive</Trans>,
  restore: <Trans>Restore</Trans>,
  edit: <Trans>Edit</Trans>,
};

const BtnIcon = {
  delete: Trash,
  copy: CopySimple,
  read: Eye,
  unread: Eye,
  archive: Archive,
  restore: ArrowArcLeft,
  edit: PencilSimple,
};

const HeaderButton = ({ type, tooltip, disabled, onClick, color }: ButtonProps) => {
  if (!type || !btnText[type]) {
    return null;
  }
  const IconComponent = BtnIcon[type];
  return (
    <Tooltip title={tooltip}>
      <div>
        <Button
          variant="tertiary"
          size="lg"
          disabled={disabled}
          onClick={() => onClick(type)}
          id={`btn${type}`}
          startIcon={<IconComponent color={color} size={20} />}
        >
          {btnText[type]}
        </Button>
      </div>
    </Tooltip>
  );
};

export const Selected = ({ options, onClick }: SelectedOptionsProps) => {
  const { table } = useTableContext();
  const selectedRows = table.getSelectedRowModel()?.flatRows;
  return (
    <>
      {options.map(option => {
        const key = typeof option === 'string' ? option : option.id;
        const disabled = typeof option === 'string' ? false : option.disabled(selectedRows);
        const hidden = typeof option === 'string' ? false : option.hidden(selectedRows);
        const tooltip =
          typeof option === 'string'
            ? ''
            : typeof option.tooltip === 'string'
              ? option.tooltip
              : option.tooltip(selectedRows);
        const color =
          typeof option === 'string'
            ? undefined
            : option.disabled(selectedRows)
              ? '#CBCCCE'
              : undefined;
        if (hidden) {
          return null;
        }
        return (
          <HeaderButton
            key={key}
            type={key}
            tooltip={tooltip}
            disabled={disabled}
            onClick={onClick}
            color={color}
          />
        );
      })}
    </>
  );
};
